/**
 * The rules. No DOM, no storage, no randomness — everything here is a pure
 * function of a `Game`, which is what makes it testable and what makes the
 * saved game in localStorage enough to rebuild the whole screen from.
 *
 * The central idea of the multi-board variants is that a `Game` holds one list
 * of guesses and several answers. The player types once and every unsolved board
 * scores it. That is the whole game: the guess that finishes board 1 is a guess
 * board 3 also had to spend, so the boards compete for the same turns.
 */

import type { BoardCount, KeyState, TileState, WordLength } from './types';
import { guessLimit } from './types';

export interface Game {
  boards: BoardCount;
  length: WordLength;
  /** One answer per board, in board order. Distinct — see `pickAnswers`. */
  answers: string[];
  /** Submitted guesses, shared by every board, oldest first. */
  guesses: string[];
}

/**
 * Colours one guess against one answer.
 *
 * Two passes, and it has to be two: the second pass may only spend letters the
 * first did not already consume. With one pass, ERROR against ORDER paints both
 * of ERROR's Rs yellow while the answer holds a green R and only one other —
 * telling the player there are three Rs in a five-letter word with two.
 */
export function scoreGuess(guess: string, answer: string): TileState[] {
  const result: TileState[] = Array.from({ length: guess.length }, () => 'absent');
  const spare = new Map<string, number>();

  for (let i = 0; i < guess.length; i += 1) {
    const letter = answer[i];
    if (letter === undefined) continue;
    if (guess[i] === letter) result[i] = 'correct';
    else spare.set(letter, (spare.get(letter) ?? 0) + 1);
  }

  for (let i = 0; i < guess.length; i += 1) {
    if (result[i] !== 'absent') continue;
    const letter = guess[i];
    if (letter === undefined) continue;
    const left = spare.get(letter) ?? 0;
    if (left > 0) {
      result[i] = 'present';
      spare.set(letter, left - 1);
    }
  }

  return result;
}

/** How many guesses this game allows in total. */
export function totalGuesses(game: Game): number {
  return guessLimit(game.boards);
}

/**
 * The index of the guess that solved a board, or null if it is still open.
 *
 * An index rather than a boolean because almost everything downstream needs the
 * number: the row a board stops scoring at, the "solved on 4" badge, and the
 * share grid all ask *when*, not *whether*.
 */
export function solvedAt(game: Game, board: number): number | null {
  const answer = game.answers[board];
  if (answer === undefined) return null;
  const index = game.guesses.indexOf(answer);
  return index === -1 ? null : index;
}

export type Status = 'playing' | 'won' | 'lost';

export function status(game: Game): Status {
  const solved = game.answers.every((_, board) => solvedAt(game, board) !== null);
  if (solved) return 'won';
  return game.guesses.length >= totalGuesses(game) ? 'lost' : 'playing';
}

/**
 * The scored rows for one board.
 *
 * Guesses after the board was solved are returned as `empty`, not scored. A
 * solved board is finished — it keeps its own rows and stops reacting, so the
 * player can still read what they did there while the other boards run on.
 */
export function boardRows(game: Game, board: number): TileState[][] {
  const answer = game.answers[board] ?? '';
  const done = solvedAt(game, board);
  return game.guesses.map((guess, row) => {
    if (done !== null && row > done) return Array.from({ length: game.length }, () => 'empty');
    return scoreGuess(guess, answer);
  });
}

const RANK: Record<KeyState, number> = {
  empty: 0,
  absent: 1,
  present: 2,
  correct: 3,
  solved: 4,
};

/**
 * What each letter has come back as, per board.
 *
 * The return is keyed by letter and holds one state per board, because that is
 * what a key on the multi-board keyboard has to draw: with four words in play a
 * single colour per key cannot be right, and picking the "best" of them would
 * hide that E is green in one word and grey in another — which is exactly the
 * thing worth knowing.
 *
 * The best state wins per board: a letter that came back green once stays green
 * even if a later guess put it in a spot where it is grey.
 */
export function keyStates(game: Game): Map<string, KeyState[]> {
  const states = new Map<string, KeyState[]>();
  const blank = (): KeyState[] => Array.from({ length: game.boards }, () => 'empty');

  /**
   * The letters the player has actually tried.
   *
   * A solved board washes out the keys it had coloured, but only those. Washing
   * every letter a–z put a wash on keys nobody had typed, and the keyboard's
   * one job is to tell "spent" from "still to try" at a glance — a key with a
   * green corner reads as a key with news on it, and the letters still worth
   * guessing were the ones hidden among them. Untried keys stay blank, which
   * also keeps them out of `wd-key-known` and off the white-lettered list.
   */
  const tried = new Set<string>();
  for (const guess of game.guesses) {
    for (const letter of guess) tried.add(letter);
  }

  game.answers.forEach((_, board) => {
    if (solvedAt(game, board) !== null) {
      // A solved board's keys say "solved" and nothing else; see `KeyState`.
      for (const letter of tried) {
        const row = states.get(letter) ?? blank();
        row[board] = 'solved';
        states.set(letter, row);
      }
      return;
    }
    const answer = game.answers[board] ?? '';
    for (const guess of game.guesses) {
      const scored = scoreGuess(guess, answer);
      for (let i = 0; i < guess.length; i += 1) {
        const letter = guess[i];
        const state = scored[i];
        if (letter === undefined || state === undefined) continue;
        const row = states.get(letter) ?? blank();
        const current = row[board] ?? 'empty';
        if (RANK[state] > RANK[current]) row[board] = state;
        states.set(letter, row);
      }
    }
  });

  return states;
}

/** Why a typed word cannot be submitted, or null if it can. */
export type Refusal = 'short' | 'unknown';

export function refuse(
  game: Game,
  typed: string,
  isLegal: (length: WordLength, word: string) => boolean,
): Refusal | null {
  if (typed.length < game.length) return 'short';
  if (!isLegal(game.length, typed)) return 'unknown';
  return null;
}

const TILE_EMOJI: Record<TileState, string> = {
  correct: '🟩',
  present: '🟨',
  absent: '⬛',
  // Rows a board never played because it was already solved. Deliberately the
  // one *hollow* square in the set, so a share of a finished quad reads as
  // boards dropping out one by one rather than as a wall of grey.
  empty: '⬜',
};

/**
 * The result as a block of text, for the clipboard.
 *
 * Boards run side by side, one column of tiles each, because they were played
 * side by side — the shape shows which board was holding everyone up. Nothing
 * in it spells a word, which is the point: it is shareable without spoiling.
 */
export function shareText(game: Game): string {
  const outcome = status(game);
  const used = outcome === 'won' ? `${game.guesses.length}` : 'X';
  const words = game.boards === 1 ? '1 word' : `${game.boards} words`;
  const header = `Wordle · ${words} × ${game.length} letters · ${used}/${totalGuesses(game)}`;
  const columns = game.answers.map((_, board) => boardRows(game, board));
  const lines = game.guesses.map((_, row) =>
    columns.map((rows) => (rows[row] ?? []).map((tile) => TILE_EMOJI[tile]).join('')).join(' '),
  );
  return [header, ...lines].join('\n');
}
